import React, { useState, useEffect, useContext } from 'react';
import { CategoryContext } from '../../providers/CategoryContext';
import { AuthContext } from '../../providers/AuthContext';
import { showError, showSuccess } from '../../utils/sweetAlert';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import EnhancedMediaLibrary from './EnhancedMediaLibrary';
import { ProductTagContext } from '../../providers/ProductTagContext';
import { getCanonicalProductTag, normalizeProductTag } from '../../config/productTags';

const EnhancedProductForm = ({ editingProduct, onCancel, onSave }) => {
  const { categories, getSizes, getColors } = useContext(CategoryContext);
  const { token } = useContext(AuthContext);
  const { tagGroups, productTags } = useContext(ProductTagContext);

  // Estados básicos del producto
  const [formData, setFormData] = useState({
    name: '', basePrice: '', stock: '', discount: 0,
    description: '', category: '', subcategory: '',
    material: '', brand: '', weight: '',
    dimensions: { length: '', width: '', height: '' },
    tags: [], hasVariants: false
  });

  // Estados para variantes
  const [variants, setVariants] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  
  // Estados para imágenes
  const [mainImages, setMainImages] = useState([]);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [currentImageTarget, setCurrentImageTarget] = useState(null);

  // Estados para UI
  const [activeTab, setActiveTab] = useState('basic');
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (editingProduct) {
      const hasNewVariants = editingProduct.hasVariants || (editingProduct.variants && editingProduct.variants.length > 0);
      const hasLegacyVariants = (editingProduct.size && editingProduct.size.length > 0) || (editingProduct.color && editingProduct.color.length > 0);
      const hasActualVariants = hasNewVariants || hasLegacyVariants;
      
      setFormData({
        name: editingProduct.name || '',
        basePrice: editingProduct.basePrice || editingProduct.price || '',
        stock: editingProduct.stock ?? '',
        discount: editingProduct.discount || 0,
        description: editingProduct.description || '',
        category: editingProduct.category?.name || editingProduct.category || '',
        subcategory: editingProduct.subcategory || '',
        material: editingProduct.material || '',
        brand: editingProduct.brand || '',
        weight: editingProduct.weight || '',
        dimensions: editingProduct.dimensions || { length: '', width: '', height: '' },
        tags: editingProduct.tags || [],
        hasVariants: hasActualVariants
      });
      
      let productVariants = [];
      if (hasNewVariants) {
        productVariants = editingProduct.variants || [];
      } else if (hasLegacyVariants) {
        const sizes = editingProduct.size || [];
        const colors = editingProduct.color || [];
        if (sizes.length > 0 && colors.length > 0) {
          sizes.forEach(size => { colors.forEach(color => { productVariants.push({ size, color, price: '', images: [] }); }); });
        } else if (sizes.length > 0) {
          sizes.forEach(size => { productVariants.push({ size, color: '', price: '', images: [] }); });
        } else if (colors.length > 0) {
          colors.forEach(color => { productVariants.push({ size: '', color, price: '', images: [] }); });
        }
      }
      setVariants(productVariants);
      setMainImages(editingProduct.images || [editingProduct.image].filter(Boolean));
    } else {
      setFormData({ name: '', basePrice: '', stock: '', discount: 0, description: '', category: '', subcategory: '', material: '', brand: '', weight: '', dimensions: { length: '', width: '', height: '' }, tags: [], hasVariants: false });
      setVariants([]); setMainImages([]);
    }
  }, [editingProduct]);

  useEffect(() => {
    if (formData.category) {
      const categoryObj = categories.find(c => c.name === formData.category);
      if (categoryObj) {
        let sizes = getSizes(categoryObj._id, formData.subcategory);
        let colors = getColors(categoryObj._id, formData.subcategory);
        if (editingProduct && (editingProduct.size?.length > 0 || editingProduct.color?.length > 0)) {
          const allSizes = [...new Set([...sizes, ...(editingProduct.size || [])])];
          const allColors = [...new Set([...colors, ...(editingProduct.color || [])])];
          setAvailableSizes(allSizes); setAvailableColors(allColors);
        } else { setAvailableSizes(sizes); setAvailableColors(colors); }
      }
    }
  }, [formData.category, formData.subcategory, categories, getSizes, getColors, editingProduct]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleDimensionChange = (dimension, value) => setFormData(prev => ({ ...prev, dimensions: { ...prev.dimensions, [dimension]: value } }));

  const addTag = (tag = tagInput) => {
    const canonicalTag = getCanonicalProductTag(tag, productTags);
    if (canonicalTag && !formData.tags.some(item => normalizeProductTag(item) === normalizeProductTag(canonicalTag))) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, canonicalTag] }));
    }
    setTagInput('');
  };
  const removeTag = (tagToRemove) => setFormData(prev => ({
    ...prev,
    tags: prev.tags.filter(tag => normalizeProductTag(tag) !== normalizeProductTag(tagToRemove))
  }));
  const toggleSuggestedTag = (tag) => {
    if (formData.tags.some(item => normalizeProductTag(item) === normalizeProductTag(tag))) {
      removeTag(tag);
      return;
    }
    addTag(tag);
  };

  const addVariant = () => setVariants(prev => [...prev, { size: '', color: '', price: '', images: [] }]);
  const updateVariant = (index, field, value) => setVariants(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
  const removeVariant = (index) => setVariants(prev => prev.filter((_, i) => i !== index));

  const generateVariants = () => {
    const newVariants = [];
    availableSizes.forEach(size => {
      availableColors.forEach(color => {
        if (!variants.some(v => v.size === size && v.color === color)) {
          newVariants.push({ size, color, price: '', images: [] });
        }
      });
    });
    setVariants(prev => [...prev, ...newVariants]);
  };

  const handleImageSelect = (image) => {
    const imageRef = image.url || image.filename;
    if (currentImageTarget === 'main') {
      setMainImages(prev => {
        if (prev.includes(imageRef)) return prev;
        return [...prev, imageRef];
      });
    } else if (typeof currentImageTarget === 'number') {
      const currentImages = variants[currentImageTarget].images;
      if (!currentImages.includes(imageRef)) {
        updateVariant(currentImageTarget, 'images', [...currentImages, imageRef]);
      }
    }
  };

  const removeImage = (imageIndex, target) => {
    if (target === 'main') { setMainImages(prev => prev.filter((_, i) => i !== imageIndex)); }
    else { updateVariant(target, 'images', variants[target].images.filter((_, i) => i !== imageIndex)); }
  };

  const moveImage = (imageIndex, direction, target) => {
    const swap = (arr) => {
      const newArr = [...arr];
      const newIndex = imageIndex + direction;
      if (newIndex < 0 || newIndex >= newArr.length) return arr;
      [newArr[imageIndex], newArr[newIndex]] = [newArr[newIndex], newArr[imageIndex]];
      return newArr;
    };
    if (target === 'main') { setMainImages(prev => swap(prev)); }
    else { updateVariant(target, 'images', swap(variants[target].images)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const productData = {
      ...formData,
      basePrice: Number(formData.basePrice), stock: Number(formData.stock),
      discount: Number(formData.discount),
      weight: formData.weight ? Number(formData.weight) : undefined,
      dimensions: {
        length: formData.dimensions.length ? Number(formData.dimensions.length) : undefined,
        width: formData.dimensions.width ? Number(formData.dimensions.width) : undefined,
        height: formData.dimensions.height ? Number(formData.dimensions.height) : undefined
      },
      images: mainImages,
      variants: formData.hasVariants ? variants.map(v => ({ ...v, price: v.price ? Number(v.price) : undefined })) : [],
      price: Number(formData.basePrice),
      image: mainImages[0] || 'https://via.placeholder.com/150',
      size: formData.hasVariants ? variants.map(v => v.size) : [],
      color: formData.hasVariants ? variants.map(v => v.color) : []
    };
    try {
      const url = editingProduct ? `${process.env.REACT_APP_API_URL}/products/${editingProduct._id}` : `${process.env.REACT_APP_API_URL}/products`;
      const method = editingProduct ? 'PUT' : 'POST';
      const response = await fetch(url, { method, headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }), body: JSON.stringify(productData) });
      if (!response.ok) throw new Error('Error al guardar el producto');
      showSuccess(editingProduct ? 'Producto actualizado con éxito' : 'Producto creado con éxito');
      onSave && onSave();
    } catch (error) { console.error('Error:', error); showError('Error al guardar el producto'); }
  };

  const TABS = [
    { key: 'basic', label: 'Información Básica', icon: 'fa-box' },
    { key: 'variants', label: 'Variantes', icon: 'fa-layer-group' },
    { key: 'images', label: 'Imágenes', icon: 'fa-images' },
    { key: 'details', label: 'Detalles', icon: 'fa-sliders' },
  ];

  return (
    <div className="prodform-panel">
      {/* Header */}
      <div className="prodform-header">
        <h2><i className="fas fa-cube"></i> {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</h2>
        <div className="prodform-tabs">
          {TABS.map(tab => (
            <button key={tab.key} className={`prodform-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)} type="button">
              <i className={`fas ${tab.icon}`}></i> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Form body */}
      <div className="prodform-body">
        <form onSubmit={handleSubmit}>
          
          {/* === TAB: Basic === */}
          {activeTab === 'basic' && (
            <div className="prodform-section">
              <div className="prodform-row">
                <div className="prodform-field">
                  <label>Nombre del Producto <span className="prodform-req">*</span></label>
                  <input type="text" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} required placeholder="Ej: Remera Metallica" />
                </div>
                <div className="prodform-field">
                  <label>Precio Base <span className="prodform-req">*</span></label>
                  <div className="prodform-input-icon">
                    <span className="prodform-currency">$</span>
                    <input type="number" step="0.01" value={formData.basePrice} onChange={(e) => handleInputChange('basePrice', e.target.value)} required />
                  </div>
                </div>
              </div>

              <div className="prodform-row">
                <div className="prodform-field">
                  <label>Categoría <span className="prodform-req">*</span></label>
                  <select value={formData.category} onChange={(e) => { handleInputChange('category', e.target.value); handleInputChange('subcategory', ''); }} required>
                    <option value="">Seleccionar categoría</option>
                    {categories.map(cat => <option key={cat._id} value={cat.name}>{cat.name}</option>)}
                  </select>
                </div>
                <div className="prodform-field">
                  {formData.category && (() => {
                    const selectedCat = categories.find(c => c.name === formData.category);
                    return selectedCat?.subcategories?.length > 0 && (
                      <>
                        <label>Subcategoría</label>
                        <select value={formData.subcategory} onChange={(e) => handleInputChange('subcategory', e.target.value)}>
                          <option value="">Seleccionar</option>
                          {selectedCat.subcategories.map(sub => <option key={sub.name} value={sub.name}>{sub.name}</option>)}
                        </select>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="prodform-row prodform-row-3">
                <div className="prodform-field">
                  <label>Stock</label>
                  <input type="number" min="0" step="1" value={formData.stock} onChange={(e) => handleInputChange('stock', e.target.value)} placeholder="Ej: 12" />
                  <span className="prodform-hint">Se sincroniza con la API y Mercado Libre</span>
                </div>
                <div className="prodform-field">
                  <label>Descuento (%)</label>
                  <input type="number" min="0" max="100" value={formData.discount} onChange={(e) => handleInputChange('discount', e.target.value)} />
                </div>
                <div className="prodform-field">
                  <label>Marca</label>
                  <input type="text" value={formData.brand} onChange={(e) => handleInputChange('brand', e.target.value)} placeholder="Ej: REM" />
                </div>
              </div>

              <div className="prodform-field">
                <label>Descripción <span className="prodform-req">*</span></label>
                <textarea rows="4" value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} required placeholder="Descripción detallada del producto..." />
              </div>

              <label className="prodform-checkbox">
                <input type="checkbox" checked={formData.hasVariants} onChange={(e) => handleInputChange('hasVariants', e.target.checked)} />
                <span className="prodform-checkmark"></span>
                Este producto tiene variantes (colores, tamaños, etc.)
              </label>
            </div>
          )}

          {/* === TAB: Variants === */}
          {activeTab === 'variants' && (
            <div className="prodform-section">
              {!formData.hasVariants ? (
                <div className="prodform-notice">
                  <i className="fas fa-info-circle"></i>
                  Activa "Este producto tiene variantes" en Información Básica para gestionar variantes.
                </div>
              ) : (
                <>
                  {editingProduct && (editingProduct.size?.length > 0 || editingProduct.color?.length > 0) && !editingProduct.hasVariants && (
                    <div className="prodform-notice prodform-notice-warn">
                      <i className="fas fa-exclamation-triangle"></i>
                      <div>
                        <strong>Variantes Legacy Detectadas</strong> — Se convertirán al nuevo sistema al guardar.
                        <br /><small>Talles: {editingProduct.size?.join(', ') || '—'} | Colores: {editingProduct.color?.join(', ') || '—'}</small>
                      </div>
                    </div>
                  )}

                  <div className="prodform-variant-header">
                    <h3>Gestión de Variantes</h3>
                    <div className="prodform-variant-btns">
                      <button type="button" className="prodform-btn-outline" onClick={generateVariants}
                        disabled={!availableSizes.length || !availableColors.length}>
                        <i className="fas fa-magic"></i> Generar Combinaciones
                      </button>
                      <button type="button" className="prodform-btn-primary" onClick={addVariant}>
                        <i className="fas fa-plus"></i> Agregar
                      </button>
                    </div>
                  </div>

                  {variants.length === 0 ? (
                    <div className="prodform-empty-variants">
                      <i className="fas fa-layer-group"></i>
                      <p>No hay variantes. Haz clic en "Agregar" para crear una.</p>
                    </div>
                  ) : (
                    <div className="prodform-variants-list">
                      {variants.map((variant, index) => (
                        <div key={index} className="prodform-variant-card">
                          <div className="prodform-variant-card-header">
                            <span className="prodform-variant-num">Variante {index + 1}</span>
                            <button type="button" className="prodform-variant-delete" onClick={() => removeVariant(index)}>
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                          <div className="prodform-variant-fields">
                            <div className="prodform-field">
                              <label>Tamaño</label>
                              <input type="text" list={`sizes-list-${index}`} value={variant.size}
                                onChange={(e) => updateVariant(index, 'size', e.target.value)}
                                placeholder="Escribe o selecciona" />
                              <datalist id={`sizes-list-${index}`}>
                                {availableSizes.map(size => <option key={size} value={size} />)}
                              </datalist>
                            </div>
                            <div className="prodform-field">
                              <label>Color</label>
                              <input type="text" list={`colors-list-${index}`} value={variant.color}
                                onChange={(e) => updateVariant(index, 'color', e.target.value)}
                                placeholder="Escribe o selecciona" />
                              <datalist id={`colors-list-${index}`}>
                                {availableColors.map(color => <option key={color} value={color} />)}
                              </datalist>
                            </div>
                            <div className="prodform-field">
                              <label>Precio Específico</label>
                              <input type="number" step="0.01" value={variant.price} onChange={(e) => updateVariant(index, 'price', e.target.value)} placeholder="Opcional" />
                            </div>
                          </div>
                          <div className="prodform-variant-images">
                            <label>Imágenes</label>
                            <div className="prodform-img-row">
                              {variant.images.map((image, imgIndex) => (
                                <div key={imgIndex} className="prodform-img-thumb">
                                  <img src={getDisplayImageUrl(image)} alt={`V${index + 1}`} />
                                  <button type="button" className="prodform-img-remove" onClick={() => removeImage(imgIndex, index)}>×</button>
                                  <div className="prodform-img-order prodform-img-order-sm">
                                    <button type="button" disabled={imgIndex === 0} onClick={() => moveImage(imgIndex, -1, index)}><i className="fas fa-chevron-left"></i></button>
                                    <button type="button" disabled={imgIndex === variant.images.length - 1} onClick={() => moveImage(imgIndex, 1, index)}><i className="fas fa-chevron-right"></i></button>
                                  </div>
                                </div>
                              ))}
                              <button type="button" className="prodform-img-add" onClick={() => { setCurrentImageTarget(index); setShowMediaLibrary(true); }}>
                                <i className="fas fa-plus"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* === TAB: Images === */}
          {activeTab === 'images' && (
            <div className="prodform-section">
              <h3>Imágenes Principales</h3>
              <div className="prodform-img-row prodform-img-main">
                {mainImages.map((image, index) => (
                  <div key={index} className="prodform-img-thumb prodform-img-thumb-lg">
                    <img src={getDisplayImageUrl(image)} alt={`Producto ${index + 1}`} />
                    <button type="button" className="prodform-img-remove" onClick={() => removeImage(index, 'main')}>×</button>
                    <div className="prodform-img-order">
                      <button type="button" disabled={index === 0} onClick={() => moveImage(index, -1, 'main')}><i className="fas fa-chevron-left"></i></button>
                      <button type="button" disabled={index === mainImages.length - 1} onClick={() => moveImage(index, 1, 'main')}><i className="fas fa-chevron-right"></i></button>
                    </div>
                  </div>
                ))}
                <button type="button" className="prodform-img-add prodform-img-add-lg" onClick={() => { setCurrentImageTarget('main'); setShowMediaLibrary(true); }}>
                  <i className="fas fa-plus"></i>
                  <span>Agregar</span>
                </button>
              </div>
            </div>
          )}

          {/* === TAB: Details === */}
          {activeTab === 'details' && (
            <div className="prodform-section">
              <div className="prodform-row">
                <div className="prodform-field">
                  <label>Material</label>
                  <input type="text" value={formData.material} onChange={(e) => handleInputChange('material', e.target.value)} placeholder="Ej: 100% Algodón" />
                </div>
                <div className="prodform-field">
                  <label>Peso (gramos)</label>
                  <input type="number" value={formData.weight} onChange={(e) => handleInputChange('weight', e.target.value)} placeholder="Ej: 200" />
                </div>
              </div>

              <div className="prodform-field">
                <label>Dimensiones (cm)</label>
                <div className="prodform-row prodform-row-3">
                  <input type="number" placeholder="Largo" value={formData.dimensions.length} onChange={(e) => handleDimensionChange('length', e.target.value)} />
                  <input type="number" placeholder="Ancho" value={formData.dimensions.width} onChange={(e) => handleDimensionChange('width', e.target.value)} />
                  <input type="number" placeholder="Alto" value={formData.dimensions.height} onChange={(e) => handleDimensionChange('height', e.target.value)} />
                </div>
              </div>

              <div className="prodform-field">
                <label>Etiquetas</label>
                <div className="prodform-tag-suggestions" aria-label="Etiquetas sugeridas">
                  {tagGroups.map(group => (
                    <div key={group.id} className="prodform-tag-group">
                      <span>{group.label}</span>
                      <div>
                        {group.tags.map(tag => {
                          const isSelected = formData.tags.some(item => normalizeProductTag(item) === normalizeProductTag(tag));
                          return (
                            <button
                              key={tag}
                              type="button"
                              className={isSelected ? 'selected' : ''}
                              onClick={() => toggleSuggestedTag(tag)}
                              aria-pressed={isSelected}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="prodform-tag-input">
                  <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Agregar etiqueta"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
                  <button type="button" onClick={addTag}><i className="fas fa-plus"></i></button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="prodform-tags-list">
                    {formData.tags.map((tag, index) => (
                      <span key={index} className="prodform-tag">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="prodform-actions">
            <button type="button" className="prodform-cancel-btn" onClick={onCancel}>Cancelar</button>
            <button type="submit" className="prodform-save-btn">
              <i className="fas fa-check"></i> {editingProduct ? 'Actualizar Producto' : 'Crear Producto'}
            </button>
          </div>
        </form>
      </div>

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="medialib-modal-overlay" onClick={() => { setShowMediaLibrary(false); setCurrentImageTarget(null); }}>
          <div className="medialib-modal prodform-medialib-modal" onClick={(e) => e.stopPropagation()}>
            <button className="medialib-modal-close" onClick={() => { setShowMediaLibrary(false); setCurrentImageTarget(null); }}>
              <i className="fas fa-times"></i>
            </button>
            <div className="prodform-medialib-content">
              <div className="prodform-medialib-header">
                <h3 style={{ fontWeight: 700, margin: 0 }}>Seleccionar Imágenes</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>
                    {currentImageTarget === 'main' ? `${mainImages.length} seleccionada(s)` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setShowMediaLibrary(false); setCurrentImageTarget(null); }}
                    style={{ padding: '8px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <i className="fas fa-check"></i> Listo
                  </button>
                </div>
              </div>
              <EnhancedMediaLibrary onSelect={handleImageSelect} mode="selection" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedProductForm;
